// EditPot.js
import React, { useState, useEffect } from "react";
import { useLanguage } from "../../../context/LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchPot,
  fetchPlantsForPot,
  deletePlantFromPot,
  deleteCustomPlantFromPot,
  updatePot,
} from "../../../api/api_pots";
import { fetchCustomPlant } from "../../../api/api_customPlants";
import { estimatedBatteryLife } from "../../../api/api_hubs";
import { fetchUserTimezoneInfo } from "../../../api/api_users";
import loadDynamicImage from "../../Entities/ImageLoader";
import defaultPlant from "../../assets/plants/defaultPlant.jpg";
import PlantDetailsModal from "../Plants/PlantDetailsModal";

/* ---------------- Timezone helpers ---------------- */

/**
 * Convert a local wall-clock hour (0-23) in a given IANA timezone to UTC hour (0-23).
 * Uses the *current* offset, which correctly handles today's DST rules.
 */
function localHourToUtc(hourLocal, iana) {
  const now = new Date();
  const localNowHour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      hour: "numeric",
      hour12: false,
    }).format(now)
  );
  const utcNowHour = now.getUTCHours();
  // local = UTC + offset  => UTC = local - offset
  const offset = (localNowHour - utcNowHour + 24) % 24;
  return (Number(hourLocal || 0) - offset + 24) % 24;
}

/**
 * Convert a UTC hour (0-23) to local wall-clock hour (0-23) in a given IANA timezone.
 */
function utcHourToLocal(hourUtc, iana) {
  const now = new Date();
  const localNowHour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: iana,
      hour: "numeric",
      hour12: false,
    }).format(now)
  );
  const utcNowHour = now.getUTCHours();
  const offset = (localNowHour - utcNowHour + 24) % 24; // local = UTC + offset
  return (Number(hourUtc || 0) + offset) % 24;
}

/* ---------------- Component ---------------- */

const EditPot = () => {
  const token = localStorage.getItem("userToken");
  const { translations } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const { hubSN, pot: initialPot, irrigation_system_enabled } = location.state;

  const [pot, setPot] = useState(initialPot);
  const [name, setName] = useState(initialPot.name);

  const [irrigationPlan, setIrrigationPlan] = useState(
    irrigation_system_enabled
      ? initialPot.irrigation_plan_times >= 2
        ? "custom"
        : String(initialPot.irrigation_plan_times)
      : "0"
  );
  const [customIrrigationDays, setCustomIrrigationDays] = useState(
    irrigation_system_enabled && initialPot.irrigation_plan_times >= 2
      ? initialPot.irrigation_plan_times
      : 0
  );

  // We'll show local hour in the input. Start with backend value; once TZ is loaded we'll convert to local.
  const [customIrrigationHour, setCustomIrrigationHour] = useState(
    irrigation_system_enabled && initialPot.irrigation_plan_times >= 1
      ? initialPot.irrigation_plan_hour
      : 1
  );

  const [irrigationOutIndex, setIrrigationOutIndex] = useState(
    irrigation_system_enabled ? initialPot.irrigation_out_index : 0
  );

  const [combinedPlants, setCombinedPlants] = useState([]);
  const [showInfoPopup, setShowInfoPopup] = useState(false);
  const [infoContent, setInfoContent] = useState("");
  const [measureTimes, setMeasureTimes] = useState(
    initialPot.measure_times || 1
  );
  const [sendTimes, setSendTimes] = useState(initialPot.send_times || 1);
  const [showPlantModal, setShowPlantModal] = useState(false);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [batteryEstimate, setBatteryEstimate] = useState(null);

  // User IANA timezone (fallback to UTC until fetched)
  const [userTimeZone, setUserTimeZone] = useState("UTC");

  const measureTimesOptions = [
    { value: 1, label: translations.half_hour },
    { value: 2, label: translations.one_hour },
    { value: 4, label: translations.two_hours },
    { value: 8, label: translations.four_hours },
  ];

  const sendTimesOptions = [
    { value: 1, label: translations.half_hour },
    { value: 2, label: translations.one_hour },
    { value: 4, label: translations.two_hours },
    { value: 8, label: translations.four_hours },
    { value: 16, label: translations.eight_hours },
    { value: 24, label: translations.twelve_hours },
    { value: 48, label: translations.one_day },
  ];

  /* ---------------- Load user's timezone ---------------- */

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const tzRes = await fetchUserTimezoneInfo();
        if (!mounted) return;
        if (!tzRes.error && tzRes.data?.iana) {
          setUserTimeZone(tzRes.data.iana);
        }
      } catch {
        // keep UTC
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  /* ---------------- Load latest pot data ---------------- */

  useEffect(() => {
    let mounted = true;
    fetchPot(initialPot.id, token).then((result) => {
      if (!mounted || !result || result.error || !result.data) return;
      const data = result.data;

      setPot(data);
      setName(data.name);

      setIrrigationPlan(
        irrigation_system_enabled
          ? data.irrigation_plan_times >= 2
            ? "custom"
            : String(data.irrigation_plan_times)
          : "0"
      );
      setCustomIrrigationDays(
        irrigation_system_enabled && data.irrigation_plan_times >= 2
          ? data.irrigation_plan_times
          : 0
      );

      // Temporarily keep backend (UTC) hour; a later effect will convert when TZ is known
      setCustomIrrigationHour(
        irrigation_system_enabled && data.irrigation_plan_times >= 1
          ? Number(data.irrigation_plan_hour ?? 1)
          : 1
      );

      setIrrigationOutIndex(
        irrigation_system_enabled ? data.irrigation_out_index : 0
      );
      setMeasureTimes(data.measure_times || 1);
      setSendTimes(data.send_times || 1);
    });
    return () => {
      mounted = false;
    };
  }, [initialPot.id, token, irrigation_system_enabled]);

  /* ---------------- Convert displayed hour (UTC -> local) once TZ is known ---------------- */

  useEffect(() => {
    if (!pot || !irrigation_system_enabled) return;
    if (
      pot.irrigation_plan_times >= 1 &&
      typeof pot.irrigation_plan_hour === "number"
    ) {
      setCustomIrrigationHour(
        utcHourToLocal(Number(pot.irrigation_plan_hour || 0), userTimeZone)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTimeZone]);

  /* ---------------- Load plants for the pot ---------------- */

  useEffect(() => {
    if (!pot) return;
    fetchPlantsForPot(pot.id, token)
      .then((data) => {
        const plantsWithCustomFlag = (data.plants || []).map((plant) => ({
          ...plant,
          isCustom: false,
        }));
        const customPlantsWithCustomFlag = (data.customPlants || []).map(
          (plant) => ({ ...plant, isCustom: true })
        );
        setCombinedPlants([
          ...plantsWithCustomFlag,
          ...customPlantsWithCustomFlag,
        ]);
      })
      .catch(() => {});
  }, [pot, token]);

  /* ---------------- Handlers ---------------- */

  const handleIrrigationPlanChange = (event) => {
    setIrrigationPlan(event.target.value);
  };

  const handleCustomDaysChange = (e) => {
    let value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 0) value = 0;
    if (value > 7) value = 7;
    setCustomIrrigationDays(value);
  };

  const handleCustomHourChange = (e) => {
    let value = parseInt(e.target.value, 10);
    if (isNaN(value) || value < 0) value = 0;
    if (value > 23) value = 23;
    setCustomIrrigationHour(value);
  };

  const handleMeasureTimesChange = (e) => {
    const value = parseFloat(e.target.value);
    setMeasureTimes(value);
    if (sendTimes < value) setSendTimes(value);
  };

  const handleSendTimesChange = (e) => {
    setSendTimes(parseFloat(e.target.value));
  };

  const handleSave = async (potSN) => {
    // Convert local UI hour -> UTC hour before sending to backend
    const utcHourToSend = localHourToUtc(
      Number(customIrrigationHour || 0),
      userTimeZone
    );

    const updatedDevice = {
      pot_sensor_device_MAC: pot.pot_sensor_device_MAC,
      plant_ids: pot.plant_ids,
      custom_plant_ids: pot.custom_plant_ids,
      name: name,
      irrigation_plan_times:
        irrigation_system_enabled && irrigationPlan === "custom"
          ? parseInt(customIrrigationDays, 10)
          : parseInt(irrigationPlan, 10),
      irrigation_plan_hour:
        irrigation_system_enabled &&
        (irrigationPlan === "custom" || irrigationPlan === "1")
          ? utcHourToSend
          : 0,
      irrigation_out_index: irrigation_system_enabled ? irrigationOutIndex : 0,
      max_temperature: pot.max_temperature,
      sunlight_level: pot.sunlight_level,
      max_ground_humidity: pot.max_ground_humidity,
      max_air_humidity: pot.max_air_humidity,
      max_minerals: pot.max_minerals,
      min_temperature: pot.min_temperature,
      min_ground_humidity: pot.min_ground_humidity,
      min_air_humidity: pot.min_air_humidity,
      min_minerals: pot.min_minerals,
      measure_times: measureTimes,
      send_times: sendTimes,
    };

    try {
      await updatePot(pot.id, updatedDevice, true, token, potSN);
      alert(translations.pot_updated_successfully);
      navigate("/spaces/spaceManager");
    } catch {
      alert(translations.failed_to_update_pot);
    }
  };

  const handleDeletePlantFromPot = (plantID, isCustom) => async (e) => {
    e.stopPropagation();
    if (window.confirm(translations.confirm_delete_plant_pot)) {
      const deleteFunction = isCustom
        ? deleteCustomPlantFromPot
        : deletePlantFromPot;
      try {
        await deleteFunction(pot.id, plantID, token);
        setCombinedPlants((prevPlants) =>
          prevPlants.filter((plant) => plant.data.id !== plantID)
        );
      } catch {
        alert(translations.error_deleting_plant);
      }
    }
  };

  const handleBack = () => navigate(-1);

  const handleAddPlantToPot = () => {
    navigate("/plants/addToPot", { state: { pot } });
  };

  const handleAdvancedSettings = (hubSN, potSN) => {
    navigate("advancedSettings", { state: { hubSN, potSN, pot } });
  };

  const handleCalibrateIrrigationTime = (hubSN, potSN) => {
    navigate("calibrateIrrigationTime", { state: { hubSN, potSN } });
  };

  const handleInfo = () => {
    setInfoContent(translations.irrigation_info);
    setShowInfoPopup(true);
  };

  const closeInfoPopup = () => {
    setShowInfoPopup(false);
  };

  const handleShowPlantDetails = async (plant) => {
    if (plant.isCustom) {
      try {
        const resp = await fetchCustomPlant(plant.id, token);
        setSelectedPlant({ ...plant, ...resp.data });
        setShowPlantModal(true);
      } catch {
        setSelectedPlant(plant);
        setShowPlantModal(true);
      }
    } else {
      setSelectedPlant(plant);
      setShowPlantModal(true);
    }
  };

  const getPlantName = (plant) =>
    plant.data?.name || plant.name || plant.common_name || "Unnamed";

  const getPlantImage = (plant) => {
    if (plant.isCustom) return defaultPlant;
    try {
      const img = loadDynamicImage(getPlantName(plant));
      return img ? img : defaultPlant;
    } catch {
      return defaultPlant;
    }
  };

  const handleCalculateBatteryDuration = async () => {
    // Normal 1000 mAh
    const normal = await estimatedBatteryLife(sendTimes, measureTimes);
    // Rechargeable 800 mAh
    const rechargeable = await estimatedBatteryLife(sendTimes, measureTimes, 800);
    setBatteryEstimate({
      normal: normal.toFixed(2) + " " + translations.months,
      rechargeable: rechargeable.toFixed(2) + " " + translations.months,
    });
  };

  /* ---------------- Render ---------------- */

  return (
    <>
      {showPlantModal && selectedPlant && (
        <PlantDetailsModal
          isCustom={selectedPlant.isCustom}
          plantName={getPlantName(selectedPlant)}
          onClose={() => setShowPlantModal(false)}
          translations={translations}
        />
      )}

      {showInfoPopup && (
        <div className="info-popup">
          <div className="info-popup-content">
            <p dangerouslySetInnerHTML={{ __html: infoContent }}></p>
            <button onClick={closeInfoPopup}>{translations.close}</button>
          </div>
        </div>
      )}

      <div className="sm-page">
        <div className="sm-container">
          <div className="header">
            <button onClick={handleBack} className="back-button">
              &#8592;
            </button>
            <h1>{translations.edit}</h1>
            <button className="header-button" onClick={handleAddPlantToPot}>
              {translations.add_plants}
            </button>
          </div>

          <div className="form-container">
            <div className="input-group full-width">
              <label htmlFor="name">{translations.name}:</label>
              <input
                id="name"
                type="text"
                className="general-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            {irrigation_system_enabled && (
              <>
                <div className="input-group full-width">
                  <label htmlFor="irrigation_plan_times">
                    {translations.irrigation_scheme}:
                  </label>
                  <div>
                    <select
                      id="irrigation_plan_times"
                      value={String(irrigationPlan)}
                      onChange={handleIrrigationPlanChange}
                      className="irrigate-select"
                    >
                      <option value="0">{translations.none}</option>
                      <option value="1">{translations.every_day}</option>
                      <option value="-1">{translations.keep_moist}</option>
                      <option value="-2">{translations.dynamic}</option>
                      <option value="custom">{translations.custom_days}</option>
                    </select>

                    {irrigationPlan === "custom" && (
                      <>
                        <input
                          type="number"
                          className="custom-days-input"
                          value={customIrrigationDays}
                          onChange={handleCustomDaysChange}
                          placeholder={translations.enter_custom_days}
                          min={0}
                          max={7}
                        />
                        <input
                          type="number"
                          className="custom-days-input"
                          value={customIrrigationHour}
                          onChange={handleCustomHourChange}
                          placeholder={translations.enter_custom_hour}
                          min={0}
                          max={23}
                        />
                      </>
                    )}

                    {irrigationPlan === "1" && (
                      <input
                        type="number"
                        className="custom-days-input"
                        value={customIrrigationHour}
                        onChange={handleCustomHourChange}
                        placeholder={translations.enter_custom_hour}
                        min={0}
                        max={23}
                      />
                    )}

                    <button onClick={handleInfo} className="info-button">
                      &#9432;
                    </button>
                  </div>
                </div>

                <div className="input-group full-width">
                  <label htmlFor="irrigation_out_index">
                    {translations.irrigation_out_index}:
                  </label>
                  <input
                    id="irrigation_out_index"
                    type="number"
                    className="general-input"
                    value={irrigationOutIndex}
                    onChange={(e) =>
                      setIrrigationOutIndex(Number(e.target.value))
                    }
                  />
                </div>

                <div className="two-col-row full-width">
                  <div className="input-group">
                    <label htmlFor="measure_times">
                      {translations.measure_times}:
                    </label>
                    <select
                      id="measure_times"
                      className="general-input"
                      value={measureTimes}
                      onChange={handleMeasureTimesChange}
                    >
                      {measureTimesOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="input-group">
                    <label htmlFor="send_times">
                      {translations.send_times}:
                    </label>
                    <select
                      id="send_times"
                      className="general-input"
                      value={sendTimes}
                      onChange={handleSendTimesChange}
                    >
                      {sendTimesOptions
                        .filter((opt) => opt.value >= measureTimes)
                        .map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="input-group full-width">
                  <label>
                    {translations.battery_duration_calculator}:
                    {batteryEstimate !== null && (
                      <span style={{ marginLeft: "8px", fontWeight: "bold" }}>
                        <br></br>
                        {(translations.no_rechargeable_battery)}: {batteryEstimate.normal}
                        <br></br>
                        {(translations.rechargeable_battery)}: {batteryEstimate.rechargeable}
                      </span>
                    )}
                  </label>
                  <div>
                    <button
                      className="general-button"
                      onClick={handleCalculateBatteryDuration}
                    >
                      {translations.calculate_battery_duration}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="sm-space-info">
            <label>{translations.plants}:</label>
            <ul className="sm-space-list">
              {combinedPlants.map((plant) => (
                <div className="help-container" key={plant.id}>
                  <div className="space-header sm-space-header">
                    <div
                      className="custom-plant-row"
                      onClick={() => handleShowPlantDetails(plant)}
                      style={{ cursor: "pointer" }}
                    >
                      <img
                        src={getPlantImage(plant)}
                        alt={getPlantName(plant)}
                        className="custom-plant-img"
                        onError={(e) => {
                          e.currentTarget.src = defaultPlant;
                        }}
                      />
                      <span className="custom-plant-name">
                        {getPlantName(plant)} {plant.isCustom ? "(CUSTOM)" : ""}
                      </span>
                    </div>
                    <div className="custom-plant-actions">
                      <button
                        onClick={handleDeletePlantFromPot(
                          plant.data.id,
                          plant.isCustom
                        )}
                        className="general-button delete-button little-button"
                      >
                        <i className="fi fi-rr-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </ul>
          </div>

          <div className="content">
            {(irrigationPlan === "custom" || irrigationPlan === "1") && (
              <button
                onClick={() => handleCalibrateIrrigationTime(hubSN, pot.SN)}
                className="general-button secondary-button"
              >
                {translations.calibrate_irrigation_time}
              </button>
            )}
          </div>

          <div className="content">
            <button
              onClick={() => handleAdvancedSettings(hubSN, pot.SN)}
              className="general-button edit-button"
            >
              {translations.advanced_settings}
            </button>
          </div>

          <div className="content">
            <button
              onClick={() => handleSave(pot.SN)}
              className="general-button"
            >
              {translations.save}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditPot;
